/**
 * Battlefield Editor Page
 *
 * Dev tool for creating, editing, and saving hex battlefield maps.
 * Supports custom grid shapes, terrain painting, spawn zone definition,
 * and isometric projection controls.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { AxialCoordinate, HexGridConfig } from '@drawn-of-war/shared';
import type { MapConfig, TerrainType, TerrainVisualStyle } from '@drawn-of-war/shared';
import { generateSpawnHexes, isEnemyPlacementArray } from '@drawn-of-war/shared';
import { EditorGridRenderer } from '../components/BattlefieldEditor/EditorGridRenderer';
import { EditorHistory } from '../components/BattlefieldEditor/editor-history';
import { applyTool, type EditorToolType, type ToolContext } from '../components/BattlefieldEditor/editor-tools';
import { BASE_TILES, OVERLAY_TILES } from '../components/BattlefieldEditor/tile-registry';
import type { BaseTileId, OverlayTileId } from '../components/BattlefieldEditor/tile-registry';
import { listMaps, loadMap, saveMap, type MapListEntry } from '../services/map-api.service';
import styles from './BattlefieldEditorPage.module.css';

// =====================================================================
// Constants
// =====================================================================

const DEFAULT_MAP: MapConfig = {
  columns: 12,
  rows: 8,
  name: 'untitled',
  playerSpawn: generateSpawnHexes([0, 1, 2], [0, 1, 2, 3, 4, 5, 6, 7]),
  enemySpawn: generateSpawnHexes([9, 10, 11], [0, 1, 2, 3, 4, 5, 6, 7]),
  terrain: [],
};

const TERRAIN_TYPES: { type: TerrainType; label: string; color: string }[] = [
  { type: 'blocked',     label: 'Blocked',     color: '#8b4513' },
  { type: 'hazard',      label: 'Hazard',      color: '#ff4500' },
  { type: 'healing',     label: 'Healing',     color: '#00ff88' },
  { type: 'cover',       label: 'Cover',       color: '#708090' },
  { type: 'difficult',   label: 'Difficult',   color: '#daa520' },
  { type: 'speed_boost', label: 'Speed Boost', color: '#00bfff' },
];

const TOOLS: { type: EditorToolType; label: string; hint: string }[] = [
  { type: 'select',      label: 'Select',       hint: 'Inspect hex' },
  { type: 'boundary',    label: 'Boundary',     hint: 'L-click add, R-click remove' },
  { type: 'terrain',     label: 'Terrain',      hint: 'Paint terrain type' },
  { type: 'playerSpawn', label: 'Player Spawn', hint: 'L-click add, R-click remove' },
  { type: 'enemySpawn',  label: 'Enemy Spawn',  hint: 'L-click add, R-click remove' },
  { type: 'erase',       label: 'Erase',        hint: 'Clear hex data' },
  { type: 'background',  label: 'Background',   hint: 'Drag to position, scroll to scale' },
  { type: 'tile',        label: 'Tile Paint',   hint: 'L-click paint, R-click erase' },
];

// =====================================================================
// Component
// =====================================================================

const BattlefieldEditorPage: React.FC = () => {
  // ----- State -----
  const [map, setMap] = useState<MapConfig>({ ...DEFAULT_MAP });
  const [selectedTool, setSelectedTool] = useState<EditorToolType>('select');
  const [terrainType, setTerrainType] = useState<TerrainType>('blocked');
  const [visualStyle, setVisualStyle] = useState<TerrainVisualStyle | undefined>(undefined);
  const [hoveredHex, setHoveredHex] = useState<AxialCoordinate | null>(null);
  const [projection, setProjection] = useState<'flat' | 'isometric'>('isometric');
  const [hexSize, setHexSize] = useState(38);
  const [savedMaps, setSavedMaps] = useState<MapListEntry[]>([]);
  const [statusMsg, setStatusMsg] = useState('');

  // Tile paint tool state
  const [selectedBaseTile, setSelectedBaseTile] = useState<BaseTileId>('grass-lush');
  const [selectedOverlay, setSelectedOverlay] = useState<OverlayTileId | undefined>(undefined);

  // Background tool state
  const [bgImage, setBgImage] = useState('');
  const [bgScale, setBgScale] = useState(1.0);
  const [bgOffsetX, setBgOffsetX] = useState(0);
  const [bgOffsetY, setBgOffsetY] = useState(0);
  const [bgBrightness, setBgBrightness] = useState(1.0);
  const [bgContrast, setBgContrast] = useState(1.0);
  const [bgSaturation, setBgSaturation] = useState(1.0);
  const [bgHue, setBgHue] = useState(0);

  // ----- Refs -----
  const canvasRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<EditorGridRenderer | null>(null);
  const historyRef = useRef(new EditorHistory());
  const mapRef = useRef(map);
  mapRef.current = map;

  const toolRef = useRef(selectedTool);
  toolRef.current = selectedTool;
  const terrainTypeRef = useRef(terrainType);
  terrainTypeRef.current = terrainType;
  const visualStyleRef = useRef(visualStyle);
  visualStyleRef.current = visualStyle;
  const selectedBaseTileRef = useRef(selectedBaseTile);
  selectedBaseTileRef.current = selectedBaseTile;
  const selectedOverlayRef = useRef(selectedOverlay);
  selectedOverlayRef.current = selectedOverlay;

  const bgScaleRef = useRef(bgScale);
  bgScaleRef.current = bgScale;
  const bgOffsetXRef = useRef(bgOffsetX);
  bgOffsetXRef.current = bgOffsetX;
  const bgOffsetYRef = useRef(bgOffsetY);
  bgOffsetYRef.current = bgOffsetY;
  const bgDragRef = useRef<{ startX: number; startY: number; startOffsetX: number; startOffsetY: number } | null>(null);

  // ----- Derived -----
  const hexGridConfig: HexGridConfig = {
    width: map.columns,
    height: map.rows,
    hexSize,
    orientation: 'flat-top',
    projection,
  };

  // ----- Map mutation helper -----
  const updateMap = useCallback((newMap: MapConfig) => {
    setMap(newMap);
    historyRef.current.push(newMap);
  }, []);

  // ----- Hex callbacks (stable refs to avoid re-creating renderer) -----
  const onHexClick = useCallback((hex: AxialCoordinate, button: 'left' | 'right') => {
    if (toolRef.current === 'background') return; // Background tool uses canvas drag/scroll, not hex clicks
    const ctx: ToolContext = {
      terrainType: terrainTypeRef.current,
      visualStyle: visualStyleRef.current,
      baseTileId: selectedBaseTileRef.current,
      overlayTileId: selectedOverlayRef.current,
    };
    const newMap = applyTool(toolRef.current, hex, mapRef.current, ctx, button);
    if (newMap !== mapRef.current) {
      updateMap(newMap);
    }
  }, [updateMap]);

  const onHexDrag = useCallback((hex: AxialCoordinate) => {
    // Same as click for paint tools
    const tool = toolRef.current;
    if (tool === 'select' || tool === 'background') return;
    const ctx: ToolContext = {
      terrainType: terrainTypeRef.current,
      visualStyle: visualStyleRef.current,
      baseTileId: selectedBaseTileRef.current,
      overlayTileId: selectedOverlayRef.current,
    };
    const newMap = applyTool(tool, hex, mapRef.current, ctx, 'left');
    if (newMap !== mapRef.current) {
      updateMap(newMap);
    }
  }, [updateMap]);

  const onHexHover = useCallback((hex: AxialCoordinate | null) => {
    setHoveredHex(hex);
  }, []);

  // ----- Initialize renderer -----
  useEffect(() => {
    if (!canvasRef.current) return;

    const container = canvasRef.current;
    const renderer = new EditorGridRenderer({
      canvasWidth: container.clientWidth,
      canvasHeight: container.clientHeight,
      hexGridConfig,
      onHexHover,
      onHexClick,
      onHexDrag,
    });

    renderer.init().then((canvas) => {
      container.appendChild(canvas);
      renderer.renderMap(map);
      historyRef.current.push(map);
    });

    rendererRef.current = renderer;

    // Keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const prev = historyRef.current.undo();
        if (prev) setMap(prev);
      }
      if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        const next = historyRef.current.redo();
        if (next) setMap(next);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // Resize handler
    const handleResize = () => {
      if (rendererRef.current && container) {
        rendererRef.current.resize(container.clientWidth, container.clientHeight);
        rendererRef.current.renderMap(mapRef.current);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      renderer.destroy();
      rendererRef.current = null;
      // Remove canvas from DOM
      while (container.firstChild) container.removeChild(container.firstChild);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Background tool drag/scroll interaction -----
  useEffect(() => {
    if (!canvasRef.current) return;
    const container = canvasRef.current;

    const onPointerDown = (e: PointerEvent) => {
      if (toolRef.current !== 'background' || !rendererRef.current) return;
      if (e.button !== 0) return;
      bgDragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startOffsetX: bgOffsetXRef.current,
        startOffsetY: bgOffsetYRef.current,
      };
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!bgDragRef.current || !rendererRef.current) return;
      const zoom = rendererRef.current.getCameraManager()?.getZoom() ?? 1;
      const dx = (e.clientX - bgDragRef.current.startX) / zoom;
      const dy = (e.clientY - bgDragRef.current.startY) / zoom;
      const newX = bgDragRef.current.startOffsetX + dx;
      const newY = bgDragRef.current.startOffsetY + dy;
      setBgOffsetX(newX);
      setBgOffsetY(newY);
      rendererRef.current.updateBackgroundPosition(newX, newY);
    };

    const onPointerUp = () => {
      bgDragRef.current = null;
    };

    const onWheel = (e: WheelEvent) => {
      if (toolRef.current !== 'background' || !rendererRef.current) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.01 : 0.01;
      const newScale = Math.max(0.05, Math.min(5.0, bgScaleRef.current + delta));
      setBgScale(newScale);
      rendererRef.current.updateBackgroundScale(newScale);
    };

    container.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    container.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('wheel', onWheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Re-render map when it changes -----
  useEffect(() => {
    if (rendererRef.current) {
      rendererRef.current.updateGridConfig(hexGridConfig);
      if (map.tiles && Object.keys(map.tiles).length > 0) {
        rendererRef.current.renderMapWithTiles(map);
      } else {
        rendererRef.current.renderMap(map);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, projection, hexSize]);

  // ----- Load map list on mount -----
  useEffect(() => {
    listMaps().then(setSavedMaps).catch(() => {});
  }, []);

  // ----- Background actions -----
  const handleBgImageLoad = useCallback(async () => {
    if (!bgImage || !rendererRef.current) return;
    try {
      await rendererRef.current.loadBackground(bgImage, bgScale, bgOffsetX, bgOffsetY);
      setStatusMsg('Background loaded');
    } catch (err) {
      setStatusMsg('Failed to load background image');
      console.error(err);
    }
  }, [bgImage, bgScale, bgOffsetX, bgOffsetY]);

  const handleBgClear = useCallback(() => {
    rendererRef.current?.clearBackground();
    setBgImage('');
    setBgScale(1.0);
    setBgOffsetX(0);
    setBgOffsetY(0);
    setBgBrightness(1.0);
    setBgContrast(1.0);
    setBgSaturation(1.0);
    setBgHue(0);
    setStatusMsg('Background cleared');
  }, []);

  // ----- Actions -----
  const handleNew = () => {
    const newMap = { ...DEFAULT_MAP, name: 'untitled' };
    updateMap(newMap);
    historyRef.current.clear();
    historyRef.current.push(newMap);
    rendererRef.current?.clearBackground();
    setBgImage('');
    setBgScale(1.0);
    setBgOffsetX(0);
    setBgOffsetY(0);
    setBgBrightness(1.0);
    setBgContrast(1.0);
    setBgSaturation(1.0);
    setBgHue(0);
    setStatusMsg('New map created');
  };

  const handleSave = async () => {
    // Include visual data from background tool
    const visual = bgImage
      ? {
          backgroundImage: bgImage, bgOffsetX, bgOffsetY, bgScale,
          bgBrightness: bgBrightness !== 1.0 ? bgBrightness : undefined,
          bgContrast: bgContrast !== 1.0 ? bgContrast : undefined,
          bgSaturation: bgSaturation !== 1.0 ? bgSaturation : undefined,
          bgHue: bgHue !== 0 ? bgHue : undefined,
        }
      : undefined;
    const mapToSave = { ...map, visual };

    if (!mapToSave.name || mapToSave.name === 'untitled') {
      const name = prompt('Map name:');
      if (!name) return;
      const namedMap = { ...mapToSave, name };
      setMap(namedMap);
      try {
        await saveMap(namedMap);
        setSavedMaps(await listMaps());
        setStatusMsg(`Saved "${name}"`);
      } catch {
        setStatusMsg('Save failed');
      }
    } else {
      try {
        await saveMap(mapToSave);
        setSavedMaps(await listMaps());
        setStatusMsg(`Saved "${mapToSave.name}"`);
      } catch {
        setStatusMsg('Save failed');
      }
    }
  };

  const handleLoad = async (name: string) => {
    try {
      const loaded = await loadMap(name);
      updateMap(loaded);
      historyRef.current.clear();
      historyRef.current.push(loaded);

      // Restore background state from loaded map
      if (loaded.visual && rendererRef.current) {
        const b = loaded.visual.bgBrightness ?? 1.0;
        const c = loaded.visual.bgContrast ?? 1.0;
        const s = loaded.visual.bgSaturation ?? 1.0;
        const h = loaded.visual.bgHue ?? 0;
        setBgImage(loaded.visual.backgroundImage);
        setBgScale(loaded.visual.bgScale);
        setBgOffsetX(loaded.visual.bgOffsetX);
        setBgOffsetY(loaded.visual.bgOffsetY);
        setBgBrightness(b);
        setBgContrast(c);
        setBgSaturation(s);
        setBgHue(h);
        // Wait for next render so grid is up to date before loading background
        setTimeout(async () => {
          try {
            if (rendererRef.current) {
              rendererRef.current.updateBackgroundColorAdjustments(b, c, s, h);
              await rendererRef.current.loadBackground(
                loaded.visual!.backgroundImage,
                loaded.visual!.bgScale,
                loaded.visual!.bgOffsetX,
                loaded.visual!.bgOffsetY,
              );
            }
          } catch (err) {
            console.warn('Failed to load background on map load:', err);
          }
        }, 100);
      } else {
        rendererRef.current?.clearBackground();
        setBgImage('');
        setBgScale(1.0);
        setBgOffsetX(0);
        setBgOffsetY(0);
        setBgBrightness(1.0);
        setBgContrast(1.0);
        setBgSaturation(1.0);
        setBgHue(0);
      }

      setStatusMsg(`Loaded "${name}"`);
    } catch {
      setStatusMsg(`Failed to load "${name}"`);
    }
  };

  const handleExportJson = () => {
    const visual = bgImage
      ? {
          backgroundImage: bgImage, bgOffsetX, bgOffsetY, bgScale,
          bgBrightness: bgBrightness !== 1.0 ? bgBrightness : undefined,
          bgContrast: bgContrast !== 1.0 ? bgContrast : undefined,
          bgSaturation: bgSaturation !== 1.0 ? bgSaturation : undefined,
          bgHue: bgHue !== 0 ? bgHue : undefined,
        }
      : undefined;
    const exportMap = { ...map, visual };
    const json = JSON.stringify(exportMap, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      setStatusMsg('JSON copied to clipboard');
    });
  };

  const handleImportJson = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const imported = JSON.parse(text) as MapConfig;
        if (!imported.columns || !imported.rows) {
          setStatusMsg('Invalid map JSON');
          return;
        }
        updateMap(imported);
        historyRef.current.clear();
        historyRef.current.push(imported);
        rendererRef.current?.clearBackground();
        setBgImage('');
        setBgScale(1.0);
        setBgOffsetX(0);
        setBgOffsetY(0);
        setStatusMsg(`Imported "${imported.name ?? 'unnamed'}"`);
      } catch {
        setStatusMsg('Failed to import — invalid JSON file');
      }
    };
    input.click();
  };

  const handleGridSizeChange = (field: 'columns' | 'rows', value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 2 || num > 40) return;

    const newMap: MapConfig = {
      ...map,
      [field]: num,
      // Reset validHexes when dimensions change
      validHexes: undefined,
    };
    updateMap(newMap);
  };

  // ----- Hex Info -----
  const getHexInfo = (hex: AxialCoordinate | null) => {
    if (!hex) return null;

    const terrainAt = map.terrain.find(t => t.positions.some(p => p.q === hex.q && p.r === hex.r));

    const inPlayerSpawn = map.playerSpawn.some(h => h.q === hex.q && h.r === hex.r);

    const inEnemySpawn = isEnemyPlacementArray(map.enemySpawn)
      ? map.enemySpawn.some(e => e.position.q === hex.q && e.position.r === hex.r)
      : map.enemySpawn.some(h => h.q === hex.q && h.r === hex.r);

    const isValid = !map.validHexes || map.validHexes.some(h => h.q === hex.q && h.r === hex.r);

    const tileAt = map.tiles?.[`${hex.q},${hex.r}`];

    return { terrainAt, inPlayerSpawn, inEnemySpawn, isValid, tileAt };
  };

  const hexInfo = getHexInfo(hoveredHex);

  // =====================================================================
  // Render
  // =====================================================================

  return (
    <div className={styles.editorContainer}>
      {/* ===== LEFT PANEL: Tools ===== */}
      <div className={styles.toolPanel}>
        <div className={styles.panelTitle}>Tools</div>
        {TOOLS.map(t => (
          <button
            key={t.type}
            className={selectedTool === t.type ? styles.toolButtonActive : styles.toolButton}
            onClick={() => setSelectedTool(t.type)}
            title={t.hint}
          >
            {t.label}
          </button>
        ))}

        {selectedTool === 'terrain' && (
          <>
            <div className={styles.panelTitle}>Terrain Type</div>
            {TERRAIN_TYPES.map(t => (
              <button
                key={t.type}
                className={terrainType === t.type ? styles.terrainOptionActive : styles.terrainOption}
                onClick={() => setTerrainType(t.type)}
              >
                <span className={styles.colorSwatch} style={{ background: t.color }} />
                {t.label}
              </button>
            ))}
          </>
        )}

        {selectedTool === 'tile' && (
          <>
            <div className={styles.panelTitle}>Base Tile</div>
            {Object.values(BASE_TILES).map(tile => (
              <button
                key={tile.id}
                className={selectedBaseTile === tile.id ? styles.terrainOptionActive : styles.terrainOption}
                onClick={() => setSelectedBaseTile(tile.id)}
              >
                <img src={tile.path} width={20} height={20} alt="" style={{ imageRendering: 'pixelated', marginRight: 6, verticalAlign: 'middle' }} />
                {tile.label}
              </button>
            ))}

            <div className={styles.panelTitle}>Overlay</div>
            <button
              className={!selectedOverlay ? styles.terrainOptionActive : styles.terrainOption}
              onClick={() => setSelectedOverlay(undefined)}
            >
              None
            </button>
            {Object.values(OVERLAY_TILES).map(tile => (
              <button
                key={tile.id}
                className={selectedOverlay === tile.id ? styles.terrainOptionActive : styles.terrainOption}
                onClick={() => setSelectedOverlay(tile.id)}
              >
                <img src={tile.path} width={20} height={20} alt="" style={{ imageRendering: 'pixelated', marginRight: 6, verticalAlign: 'middle' }} />
                {tile.label}
              </button>
            ))}
          </>
        )}
      </div>

      {/* ===== CENTER: Canvas ===== */}
      <div className={styles.canvasArea}>
        <div ref={canvasRef} style={{ width: '100%', height: '100%' }} />
        <div className={styles.statusBar}>
          <span>
            {hoveredHex ? `q: ${hoveredHex.q}  r: ${hoveredHex.r}` : 'No hex selected'}
          </span>
          <span>{map.columns}x{map.rows}</span>
          <span>{projection}</span>
          {statusMsg && <span style={{ color: '#00d4ff' }}>{statusMsg}</span>}
        </div>
      </div>

      {/* ===== RIGHT PANEL: Properties ===== */}
      <div className={styles.propsPanel}>
        {/* Map Info */}
        <div className={styles.panelTitle}>Map</div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Name</span>
          <input
            className={styles.fieldInput}
            value={map.name ?? ''}
            onChange={e => setMap({ ...map, name: e.target.value })}
          />
        </div>

        <div className={styles.divider} />

        {/* Grid Settings */}
        <div className={styles.panelTitle}>Grid</div>
        <div className={styles.fieldRow}>
          <span className={styles.fieldRowLabel}>W</span>
          <input
            className={styles.fieldInputSmall}
            type="number"
            min={2} max={40}
            value={map.columns}
            onChange={e => handleGridSizeChange('columns', e.target.value)}
          />
          <span className={styles.fieldRowLabel}>H</span>
          <input
            className={styles.fieldInputSmall}
            type="number"
            min={2} max={40}
            value={map.rows}
            onChange={e => handleGridSizeChange('rows', e.target.value)}
          />
        </div>

        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Projection</span>
          <select
            className={styles.selectInput}
            value={projection}
            onChange={e => setProjection(e.target.value as 'flat' | 'isometric')}
          >
            <option value="isometric">Isometric</option>
            <option value="flat">Flat (top-down)</option>
          </select>
        </div>

        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Hex Size: {hexSize}px</span>
          <input
            type="range"
            min={20} max={60} step={1}
            value={hexSize}
            onChange={e => setHexSize(parseInt(e.target.value, 10))}
          />
        </div>

        <div className={styles.divider} />

        {/* Hex Inspector */}
        <div className={styles.panelTitle}>Hex Inspector</div>
        {hoveredHex ? (
          <div className={styles.hexInfo}>
            <div><span className={styles.hexInfoLabel}>Coord:</span> ({hoveredHex.q}, {hoveredHex.r})</div>
            <div><span className={styles.hexInfoLabel}>Valid:</span> {hexInfo?.isValid ? 'Yes' : 'No'}</div>
            <div><span className={styles.hexInfoLabel}>Terrain:</span> {hexInfo?.terrainAt?.type ?? 'normal'}</div>
            <div><span className={styles.hexInfoLabel}>P.Spawn:</span> {hexInfo?.inPlayerSpawn ? 'Yes' : 'No'}</div>
            <div><span className={styles.hexInfoLabel}>E.Spawn:</span> {hexInfo?.inEnemySpawn ? 'Yes' : 'No'}</div>
            <div><span className={styles.hexInfoLabel}>Base Tile:</span> {hexInfo?.tileAt?.baseTile ?? 'none'}</div>
            <div><span className={styles.hexInfoLabel}>Overlay:</span> {hexInfo?.tileAt?.overlay ?? 'none'}</div>
          </div>
        ) : (
          <div className={styles.hexInfo} style={{ fontStyle: 'italic' }}>Hover a hex to inspect</div>
        )}

        <div className={styles.divider} />

        {/* Actions */}
        <div className={styles.panelTitle}>Actions</div>
        <button className={styles.actionButtonPrimary} onClick={handleSave}>Save</button>
        <button className={styles.actionButton} onClick={handleExportJson}>Export JSON</button>
        <button className={styles.actionButton} onClick={handleImportJson}>Import JSON</button>
        <button className={styles.actionButton} onClick={handleNew}>New Map</button>

        <div className={styles.divider} />

        {/* Background */}
        <div className={styles.panelTitle}>Background</div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Image Path</span>
          <input
            className={styles.fieldInput}
            placeholder="/assets/challenges/..."
            value={bgImage}
            onChange={e => setBgImage(e.target.value)}
          />
        </div>
        <button className={styles.actionButton} onClick={handleBgImageLoad}>Load Image</button>
        {rendererRef.current?.getBackgroundVisual() && (
          <div className={styles.hexInfo}>
            <div><span className={styles.hexInfoLabel}>Scale:</span> {bgScale.toFixed(3)}</div>
            <div><span className={styles.hexInfoLabel}>Offset X:</span> {Math.round(bgOffsetX)}</div>
            <div><span className={styles.hexInfoLabel}>Offset Y:</span> {Math.round(bgOffsetY)}</div>
          </div>
        )}
        {rendererRef.current?.getBackgroundVisual() && (
          <>
            {[
              { label: 'Brightness', value: bgBrightness, set: setBgBrightness, min: 0, max: 2, step: 0.01, fmt: (v: number) => `${Math.round(v * 100)}%` },
              { label: 'Contrast', value: bgContrast, set: setBgContrast, min: 0, max: 2, step: 0.01, fmt: (v: number) => `${Math.round(v * 100)}%` },
              { label: 'Saturation', value: bgSaturation, set: setBgSaturation, min: 0, max: 2, step: 0.01, fmt: (v: number) => `${Math.round(v * 100)}%` },
              { label: 'Hue', value: bgHue, set: setBgHue, min: -180, max: 180, step: 1, fmt: (v: number) => `${Math.round(v)}°` },
            ].map(s => (
              <div className={styles.fieldGroup} key={s.label}>
                <span className={styles.fieldLabel}>{s.label} {s.fmt(s.value)}</span>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={s.value}
                  onChange={e => {
                    const val = parseFloat(e.target.value);
                    s.set(val);
                    const b = s.label === 'Brightness' ? val : bgBrightness;
                    const c = s.label === 'Contrast' ? val : bgContrast;
                    const sat = s.label === 'Saturation' ? val : bgSaturation;
                    const h = s.label === 'Hue' ? val : bgHue;
                    rendererRef.current?.updateBackgroundColorAdjustments(b, c, sat, h);
                  }}
                  style={{ width: '100%' }}
                />
              </div>
            ))}
          </>
        )}
        <button className={styles.actionButton} onClick={handleBgClear}>Clear Background</button>

        <div className={styles.divider} />

        {/* Load */}
        <div className={styles.panelTitle}>Load Map</div>
        {savedMaps.length > 0 ? (
          savedMaps.map((m, i) => (
            <button
              key={`${m.source}-${m.name}-${i}`}
              className={styles.actionButton}
              onClick={() => handleLoad(m.name)}
              style={{ textAlign: 'left', fontSize: 12 }}
            >
              {m.name}
              <span style={{ color: '#5a7a9a', marginLeft: 4 }}>({m.source})</span>
            </button>
          ))
        ) : (
          <div className={styles.hexInfo} style={{ fontStyle: 'italic' }}>No maps available</div>
        )}
      </div>
    </div>
  );
};

export default BattlefieldEditorPage;
